// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the parts of the Agents settings pages (design/agents-machine-settings A, JP 2026-11-04): a
// titled Lens panel of one-line rows, each with a switch, or a value that opens its choices. Phones
// get the glass menu from the row pressed; desktop the same groups as a menu under the row.

import styles from '@app/features/konvo_agents/components/AgentSettingsRows.module.css';
import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {GlassMenu} from '@app/features/ui/glass_menu/GlassMenu';
import type {MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {IconChevronRight} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useState} from 'react';

const MENU_WIDTH = 320;

export function SettingsGroup({
	title,
	action,
	children,
	...rest
}: {
	title?: React.ReactNode;
	action?: React.ReactNode;
	children: React.ReactNode;
	'data-flx'?: string;
}) {
	return (
		<section className={styles.group} {...rest}>
			{title && (
				<div className={styles.heading}>
					<h4 className={styles.headingText}>{title}</h4>
					{action}
				</div>
			)}
			<div className={styles.panel}>{children}</div>
		</section>
	);
}

interface RowText {
	icon: React.ReactNode;
	title: React.ReactNode;
	description?: React.ReactNode;
}

function RowBody({icon, title, description}: RowText) {
	return (
		<>
			<span className={styles.icon}>{icon}</span>
			<span className={styles.text}>
				<span className={styles.title}>{title}</span>
				{description && <span className={styles.description}>{description}</span>}
			</span>
		</>
	);
}

/** A row: only words and a quiet value, or (with onClick) something to press. */
export function SettingsRow({
	icon,
	title,
	description,
	trailing,
	onClick,
	disabled,
	chevron = onClick !== undefined,
	className,
	...rest
}: RowText & {
	trailing?: React.ReactNode;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
	chevron?: boolean;
	className?: string;
	'aria-expanded'?: boolean;
	'data-flx'?: string;
	'data-machine'?: string;
}) {
	const end = (
		<>
			{trailing}
			{chevron && <IconChevronRight size={16} stroke={2} className={styles.chevron} aria-hidden="true" />}
		</>
	);
	if (!onClick)
		return (
			<div className={clsx(styles.row, className)} {...rest}>
				<RowBody icon={icon} title={title} description={description} />
				{end}
			</div>
		);
	return (
		<button
			type="button"
			className={clsx(styles.row, styles.rowButton, className)}
			onClick={onClick}
			disabled={disabled}
			{...rest}
		>
			<RowBody icon={icon} title={title} description={description} />
			{end}
		</button>
	);
}

export function SwitchRow({
	icon,
	title,
	description,
	value,
	disabled,
	onChange,
	...rest
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
	value: boolean;
	disabled?: boolean;
	onChange: (value: boolean) => void;
	'data-flx'?: string;
}) {
	return (
		<div className={clsx(styles.row, styles.switchRow)} {...rest}>
			<span className={styles.icon}>{icon}</span>
			<Switch
				className={styles.switch}
				label={title}
				description={description}
				value={value}
				disabled={disabled}
				onChange={onChange}
				compact
			/>
		</div>
	);
}

/** A row showing the current choice; pressing it opens the choices. */
export function ChoiceRow({
	icon,
	title,
	description,
	value,
	groups,
	disabled,
	...rest
}: RowText & {
	value: React.ReactNode;
	groups: (onClose: () => void) => Array<MenuGroupType>;
	disabled?: boolean;
	'data-flx'?: string;
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const close = () => setAnchor(null);
	const press = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (MobileLayout.enabled) {
			setAnchor(event.currentTarget);
			return;
		}
		ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => <DataMenuRenderer groups={groups(onClose)} />);
	};
	return (
		<>
			<SettingsRow
				icon={icon}
				title={title}
				description={description}
				trailing={<span className={styles.value}>{value}</span>}
				onClick={press}
				disabled={disabled}
				{...rest}
			/>
			{anchor && (
				<GlassMenu
					isOpen
					onClose={close}
					anchor={anchor}
					width={MENU_WIDTH}
					groups={groups(close)}
					data-flx={rest['data-flx'] ? `${rest['data-flx']}.menu` : undefined}
				/>
			)}
		</>
	);
}
